import {
  compose,
  withStateHandlers,
  withPropsOnChange,
  withProps,
  branch,
  renderNothing,
  withHandlers,
} from "recompose";
import { ApiClient } from "services";
import { NotificationManager } from "react-notifications";
import { actions } from "react-redux-form";
import { noop, isFunction, get, pickBy, flatten } from "lodash";

const process_errors = (data, pre = null) => {
  const output = [];
  if (Array.isArray(data)) {
    const e = data.map((error) => process_errors(error, pre));
    e.forEach((i) => output.push(i));
  } else {
    Object.keys(data).forEach((key) => {
      if (key !== "message" && key !== "code") {
        const e = process_errors(data[key], pre ? [pre, key].join(".") : key);
        e.forEach((i) => output.push(i));
      } else if (key === "message") {
        output.push({
          model: pre,
          message: data["message"],
          code: data["code"] || null,
        });
      }
    });
  }
  return flatten(output);
};

const api_errors_to_form_errors = (dispatch, models) => (err) => {
  const data = get(err, "response.data");
  process_errors(data).forEach(({ model, message, code }) => {
    if (models.indexOf(model) < 0) {
      return;
    }
    if (code) {
      dispatch(actions.setErrors("local." + model, { [code]: message }));
    } else {
      dispatch(actions.setErrors("local." + model, message));
    }
  });
};

export default ({
  url,
  singular: defaultSingular,
  defaultItem,
  removeNullKeys = true,
  onFetchFailed = (err) => {
    throw err;
  },
  beforeSave,
  afterFetched,
  saveErrorHandler,
  redirectToSavedItem = true,
  convertServerErrors = false,
} = {}) =>
  compose(
    withStateHandlers(
      {
        form: null,
        item: null,
        pending: false,
        dispatch: null,
      },
      {
        setItem: () => (item) => ({ item }),
        setForm: () => (form) => ({ form }),
        setDispatch: () => (dispatch) => ({ dispatch }),
      }
    ),
    withProps(({ match, item, readOnly, t, ...props }) => {
      const resourceId = get(match, "params.id");
      const singular = props.singular || defaultSingular;
      let mode;
      if (readOnly) {
        mode = t("View");
      } else {
        mode = item && item.id ? t("Edit") : t("New");
      }

      return {
        resourceId: resourceId === "new" ? null : resourceId,
        singular,
        mode,
        title: `${mode} ${singular}`,
      };
    }),
    withPropsOnChange(["resourceId"], (props) => {
      const { setItem, resourceId } = props;

      if (!resourceId) {
        const item = isFunction(defaultItem) ? defaultItem(props) : defaultItem;
        Promise.resolve(item || {}).then((resolvedItem) =>
          setItem(resolvedItem)
        );
      } else {
        const client = ApiClient();
        client
          .get(`${url}/${resourceId}`)
          .tap((resp) => {
            let data = resp.data;

            if (removeNullKeys) {
              data = pickBy(data, (i) => i !== null);
            }
            setItem(
              isFunction(afterFetched) ? afterFetched(props)(data) : data
            );
          })
          .catch(onFetchFailed);
      }
    }),
    withHandlers({
      onBack:
        ({ history }) =>
        () =>
          history.goBack(),
      change:
        ({ dispatch }) =>
        (model, value) =>
          dispatch(actions.change(`local.${model}`, value)),
      merge:
        ({ dispatch }) =>
        (values, model = "local") =>
          Object.keys(values).forEach((key) =>
            dispatch(actions.change(`${model}.${key}`, values[key]))
          ),
      onUpdate:
        ({ setForm }) =>
        (form) =>
          setForm(form),
      onChange:
        ({ setItem }) =>
        (item) =>
          setItem(item),
      setDispatch:
        ({ setDispatch }) =>
        (dispatch) =>
          setDispatch(dispatch),
      onSubmit: (props) => () => {
        const {
          resourceId,
          dispatch: tmpDispatch,
          match,
          item: tmpItem,
          history,
          setServerErrors,
          t,
        } = props;
        const client = ApiClient();
        let promise = null;
        const isNew = !resourceId;
        const dispatch = tmpDispatch || noop;

        const item = isFunction(beforeSave)
          ? beforeSave(props)(tmpItem)
          : tmpItem;

        if (isNew) {
          promise = client.post(url, item);
        } else {
          promise = client.put(`${url}/${resourceId}`, item);
        }

        dispatch(actions.setPending("local", true));

        if (isFunction(setServerErrors)) setServerErrors({});

        promise = promise
          .tap((resp) => {
            if (isNew) {
              NotificationManager.success(t("Added a new group successfully"));
            } else {
              NotificationManager.success(t("Updated the group successfully"));
            }

            const id = get(resp, "data.id");

            if (isNew && !redirectToSavedItem) {
              history.replace(match.url);
            } else if (isNew && id) {
              const newUrl = match.url.replace("/new", `/${id}`);
              history.replace(newUrl);
            }
          })
          .catch((err) => {
            const status = get(err, "response.status");
            if (isFunction(setServerErrors)) setServerErrors(err);

            if (convertServerErrors) {
              api_errors_to_form_errors(dispatch, convertServerErrors)(err);
            }

            if (isFunction(saveErrorHandler)) {
              return saveErrorHandler(props)(err);
            }

            if (status === 400) {
              const errorData = get(err, "response.data");
              if (errorData && errorData.name && errorData.name[0] === "group with this name already exists.") {
                NotificationManager.error("Group name already exists.", "Error");
              } else {
                NotificationManager.error(
                  t("Failed to save, please check the supplied information is correct")
                );
              }
            } else if (status === 500) {
              NotificationManager.error(t("An unexpected error occurred"));
            } else {
              NotificationManager.error(t("Failed to save"));
            }
            err.handled = true;
            throw err;
          })
          .finally(() => {
            dispatch(actions.setSubmitted("local", true));
          });

        return promise;
      },
    }),
    branch((props) => !props.item, renderNothing)
  );
